import {
  Button,
  ButtonSize,
  ButtonVariant,
  Hide,
  Show
} from "shared/components";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { Row } from "views/applications-id-report/components/Row";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { useState } from "react";
import { Unit } from "types/landlord/address";
import { SectionCard } from "shared/app/SectionCard";
import { useFormTyped, type Register } from "shared/hooks";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { getErrorMessage } from "utils/error";
import { UnitGeneralForm } from "shared/app/UnitGeneralForm";
import { PMSUnitGeneralForm } from "shared/app/UnitGeneralForm/PMSUnitGeneralForm";
import * as yup from "yup";
import { PropertyKind } from "shared/types";
import { useAuthContext } from "providers/Auth";
import { AddressValidationStatus } from "shared/app/AddressValidationStatus";
import { useEditDetailsPermissions } from "shared/hooks/use-edit-details-permissions";

interface GeneralOverrides {
  title?: boolean;
  description?: boolean;
  address?: boolean;
}

// A PMS field is only required once the partner switched it to "Edit
// manually". While it is synced it is read-only and simply mirrors whatever
// the PMS sent, which may legitimately be empty — requiring it there would
// block saving every other override on a value the partner cannot fix.
const requiredWhenManual = (key: keyof GeneralOverrides, message: string) =>
  yup.string().when("pms_overrides", {
    is: (overrides?: GeneralOverrides) => overrides?.[key] === false,
    then: (schema) => schema.required(message),
    otherwise: (schema) => schema.optional()
  });

// The label is passed in so the error reads "Site name is required" for
// Site-terminology partners, matching the field label above it.
const requiredWithStreet = (message: string) =>
  yup.string().when("address1", {
    is: (address1: string) => Boolean(address1),
    then: (schema) => schema.required(message),
    otherwise: (schema) => schema.optional()
  });

const getGeneralValidationSchema = (unitLabel: string) =>
  yup.object().shape({
    title: yup.string().required(`${unitLabel} name is required`),
    address1: yup.string().optional(),
    address2: yup.string().optional(),
    city: requiredWithStreet("City is required"),
    state_or_province: requiredWithStreet("State is required"),
    postal_code: requiredWithStreet("Zip is required")
  });

const getPmsGeneralValidationSchema = (unitLabel: string) =>
  yup.object().shape({
    title: requiredWhenManual("title", `${unitLabel} name is required`),
    address1: requiredWhenManual("address", "Street address is required"),
    address2: yup.string().optional(),
    city: requiredWhenManual("address", "City is required"),
    state_or_province: requiredWhenManual("address", "State is required"),
    postal_code: requiredWhenManual("address", "Zip is required")
  });

// A type alias, not an interface: the PMS form takes the form values as
// `Record<string, unknown>` and only aliases get an implicit index signature.
type GeneralFormValues = {
  externalId: string;
  title: string;
  description: string;
  address1: string;
  address2: string;
  city: string;
  state_or_province: string;
  postal_code: string;
  pms_values?: Unit["pms_values"];
  pms_overrides?: Unit["pms_overrides"];
};

interface EditGeneralFormProps {
  onClose: () => void;
  unitId: number;
  propertyKind: PropertyKind | null;
  unitPmsId?: string;
  initialValues: GeneralFormValues;
}

// Only send the address while the group is set to "Edit manually". When it is
// synced the API restores it from the PMS snapshot on its own, and sending a
// value would create a unit-level address record for units that never had one.
// `all_or_none_of` on the API also rejects a partial address, so skip the
// group when there is nothing to send.
const buildAddressPayload = (values: GeneralFormValues) => {
  if (values.pms_overrides?.address !== false) return {};
  if (!values.address1) return {};

  return {
    address: {
      address1: values.address1,
      address2: values.address2,
      city: values.city,
      state_or_province: values.state_or_province,
      postal_code: values.postal_code
    }
  };
};

const EditGeneralForm = ({
  onClose,
  unitId,
  unitPmsId,
  propertyKind,
  initialValues
}: EditGeneralFormProps) => {
  const queryClient = useQueryClient();
  const { type } = useAuthContext();
  const { Unit: unitLabel } = useUnitsPropertiesDictionary();
  const isPmsUnit = type.isPMS && Boolean(unitPmsId);

  const { register, values, onSubmit } = useFormTyped(
    initialValues,
    isPmsUnit
      ? getPmsGeneralValidationSchema(unitLabel)
      : getGeneralValidationSchema(unitLabel)
  );

  const { mutate: updateUnit, isPending } = useMutation({
    mutationFn: (submitted: GeneralFormValues) => {
      if (!isPmsUnit)
        return addressApi.editUnit(unitId, {
          external_id: submitted.externalId,
          title: submitted.title,
          description: submitted.description,
          ...(submitted.address1
            ? {
                address: {
                  address1: submitted.address1,
                  address2: submitted.address2,
                  city: submitted.city,
                  state_or_province: submitted.state_or_province,
                  postal_code: submitted.postal_code
                }
              }
            : {})
        });

      // Site ID stays locked, so it is never part of the PMS payload. Title and
      // description are always sent: when a field is synced they carry the PMS
      // value the partner sees, so the stored column matches the reference.
      return addressApi.editUnit(unitId, {
        title: submitted.title,
        description: submitted.description,
        pms_overrides: submitted.pms_overrides,
        ...buildAddressPayload(submitted)
      });
    },
    onSuccess: () => {
      toast.success("Unit updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["unit"]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update unit: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: (submitted) => {
      updateUnit(submitted);
    },
    onError: () => {
      toast.error("Form validation failed");
    }
  });

  const footer = (
    <SaveCancelButtons
      onSave={(e) => handleSubmit(e)}
      onCancel={onClose}
      saveLoading={isPending}
    />
  );

  if (isPmsUnit)
    return (
      <PMSUnitGeneralForm
        register={register as unknown as Register}
        values={values}
        unitPmsId={unitPmsId}
        propertyKind={propertyKind}
        footer={footer}
      />
    );

  return (
    <UnitGeneralForm
      register={register as unknown as Register}
      propertyKind={propertyKind}
      pmsId={unitPmsId}
      showAddress
      footer={footer}
    />
  );
};

export const GeneralSection = ({ unit }: { unit: Unit }) => {
  // Singular, so Site-terminology partners read "Site name" / "Site ID".
  const { Unit: UnitLabel } = useUnitsPropertiesDictionary();
  const [isEditing, setIsEditing] = useState(false);
  const { type } = useAuthContext();
  const { canEditUnitDetails, unitEditDeniedTooltip } =
    useEditDetailsPermissions();
  const address = unit.address ?? unit.property.address;

  // The read-only rows below fall back to the property address for units that
  // never had one of their own, but the form must not: prefilling it would
  // silently persist the property address as a brand-new unit-level address
  // record the moment the partner switches the Address group to "Edit
  // manually". Fall back to the PMS snapshot instead — that is what a synced
  // unit is meant to mirror — and otherwise start blank.
  const formAddress = unit.address ?? unit.pms_values?.address;

  // Same gate the Rental Information override uses: a PMS-connected unit is
  // only editable for partners on Boom Screen. Without it the section stays
  // read-only, exactly as before. `isPMS` is part of the gate so the button can
  // never open a form that has no override UI in it.
  const canEditPmsUnit = type.isPMS && type.isBoomScreen;

  if (isEditing)
    return (
      <EditGeneralForm
        onClose={() => setIsEditing(false)}
        unitId={unit.id}
        propertyKind={unit.property.kind}
        unitPmsId={unit.pms_id}
        initialValues={{
          externalId: unit.external_id || "",
          title: unit.title || "",
          description: unit.description || "",
          address1: formAddress?.address1 || "",
          address2: formAddress?.address2 || "",
          city: formAddress?.city || "",
          state_or_province: formAddress?.state_or_province || "",
          postal_code: formAddress?.postal_code || "",
          pms_values: unit.pms_values,
          pms_overrides: unit.pms_overrides
        }}
      />
    );

  return (
    <SectionCard
      title="General"
      headerExtra={
        <Hide if={Boolean(unit.pms_id) && !canEditPmsUnit}>
          <Button
            size={ButtonSize.SM}
            variant={ButtonVariant.BASE}
            onClick={() => setIsEditing(true)}
            disabled={!canEditUnitDetails}
            tooltip={unitEditDeniedTooltip}
            tooltipPlacement="bottom-end"
          >
            Edit
          </Button>
        </Hide>
      }
    >
      <div className="space-y-4">
        <Row title={`${UnitLabel} name`} value={unit.title} />
        <Row title={`${UnitLabel} ID`} value={unit.external_id} />
        <Row title="Street address" value={address.address1} />
        <Row title="Apartment, suite, etc" value={address.address2} />
        <Row title="State" value={address.state_or_province} />
        <Row title="City" value={address.city} />
        <Row title="Zip" value={address.postal_code} />
        <Show if={type.isAddressValidationEnabled}>
          <Row
            title="Address validation"
            value={
              <div className="space-y-1">
                <AddressValidationStatus address={address} />
                <Show if={Boolean(unit.address_inherited_from_property)}>
                  <p className="text-sm text-content-700">
                    This {UnitLabel.toLowerCase()} has no address of its own, so
                    this is the result for the property&apos;s address.
                  </p>
                </Show>
              </div>
            }
            valueClassName="w-[500px]"
          />
        </Show>
        <Show if={unit.property.kind || type.isPmsListingSyncEnabled}>
          <Row
            title="Description"
            value={unit.description}
            valueClassName="w-[500px] whitespace-pre-wrap"
          />
        </Show>
      </div>
    </SectionCard>
  );
};
